import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CenterMembershipModal = ({ user, centers, currentMemberships, onClose, onSave, loading }) => {
  const [selectedCenterIds, setSelectedCenterIds] = useState([]);

  useEffect(() => {
    const existing = currentMemberships?.map((m) => m?.centerId || m?.center_id) || [];
    setSelectedCenterIds(existing);
  }, [currentMemberships]);

  const toggleCenter = (centerId) => {
    setSelectedCenterIds((prev) =>
      prev?.includes(centerId) ? prev?.filter((id) => id !== centerId) : [...prev, centerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCenterIds?.length === centers?.length) {
      setSelectedCenterIds([]);
    } else {
      setSelectedCenterIds(centers?.map((c) => c?.id));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    await onSave?.(user?.id, selectedCenterIds);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Manage Center Memberships</h2>
            <p className="text-sm text-gray-500 mt-0.5">{user?.fullName} — {user?.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <Icon name="X" size={22} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="px-6 py-4 flex-1 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">
                {selectedCenterIds?.length} of {centers?.length} centers selected
              </span>
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {selectedCenterIds?.length === centers?.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="space-y-2">
              {centers?.map((center) => {
                const isSelected = selectedCenterIds?.includes(center?.id);
                return (
                  <div
                    key={center?.id}
                    onClick={() => toggleCenter(center?.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-blue-300 bg-blue-50' :'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                      }`}
                    >
                      {isSelected && <Icon name="Check" size={12} className="text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{center?.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {center?.city || center?.location || ''}
                        {center?.centerType ? ` • ${center?.centerType?.replace(/_/g, ' ')}` : ''}
                      </div>
                    </div>
                    {isSelected && (
                      <span className="text-xs font-medium text-blue-600 flex-shrink-0">Assigned</span>
                    )}
                  </div>
                );
              })}
              {centers?.length === 0 && (
                <div className="text-center py-8">
                  <Icon name="Building" size={36} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-gray-500 text-sm">No centers available</p>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Memberships'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CenterMembershipModal;
